import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { CFG } from '../core/config.js';
import { KIND, ST } from '../core/entities.js';
import { variantTint } from '../chars/variants.js';
import { animAssets, KIND_TO_VAT } from '../anim/animation.js';
import { ARCHES, COLLAPSE, buildArchGeometry } from './geometry.js';
import { buildFactionAtlas } from './atlas.js';
import { makeCrowdMaterial, makeCrowdDepthMaterial } from './material.js';

/**
 * ===========================================================================
 *  CROWD RENDERER — publishes `world.crowd`
 * ===========================================================================
 *
 * 500+ animated warriors, ~1 draw call per (archetype, LOD) bucket.
 *
 *   - every clip of every character kind is pre-baked into ONE float texture
 *     of bone matrices (src/anim/vat.js);
 *   - each bucket is a single THREE.InstancedMesh whose vertex shader reads
 *     that texture (src/crowd/material.js);
 *   - both factions share one material because the faction albedos are
 *     composited into the quadrants of a single atlas (src/crowd/atlas.js);
 *   - LOD is chosen per unit with hysteresis so nothing pops, and units are
 *     sphere-culled against the camera frustum on the CPU so anything
 *     off-screen costs nothing at all.
 *
 * Per-frame contract (unchanged): reads entities x/y/z/yaw/scale/fade/flash
 * plus the animation state published by `world.anim`.
 *
 *   crowd.sync()      force a rebuild of the instance buffers
 *   crowd.setMax(n)   cap the number of drawn units
 *   crowd.visible     units drawn last frame
 *   crowd.stats       { lod0, lod1, lod2, culled, drawn, groups, triangles }
 *   crowd.groups      the InstancedMesh list (debug / capture scripts)
 * ===========================================================================
 */

const NLOD = 3;
const LOD_DIST = [15, 38];        // LOD0 -> LOD1 -> LOD2
const LOD_HYST = 4.0;
const CAPS = [72, 240, 460];      // instances per (archetype, lod) bucket
const OCC_CLEAR_HOLD = 0.06;
const OCC_MAX_DT = 0.1;

const _pm = new THREE.Matrix4();
const _frustum = new THREE.Frustum();
const _col = new THREE.Color();
const _a5 = new Float32Array(5);
const planes = new Float32Array(24);
const _lodN = new Int32Array(3);

export function createCrowd() {
  const N = 2600;
  const cache = {
    uid: new Int32Array(N), arch: new Int8Array(N), lod: new Int8Array(N),
    tint: new Float32Array(N * 3), seed: new Float32Array(N),
    occ: new Float32Array(N), occHold: new Float32Array(N), occMask: new Uint8Array(N),
  };
  cache.occ.fill(1);
  const groups = [];                 // flat InstancedMesh list (public)
  const bucket = [];                 // bucket[arch][lod] -> record
  let built = false, vat = null, material = null, depthMat = null, atlas = null;
  let api = null;
  let maxUnits = CFG.q.crowdMax || 600;
  let totalTris = 0, buildMs = 0;

  /* --------------------------------------------------------------- build */

  function build(w) {
    const chars = w.chars;
    if (!chars || !chars.variantGeo) return false;
    const t0 = performance.now();
    const assets = animAssets(w);
    vat = assets.vat;
    atlas = buildFactionAtlas(chars, w.renderer);
    material = makeCrowdMaterial(chars.materialForTeam(0), vat, atlas);
    depthMat = makeCrowdDepthMaterial(vat);

    for (let a = 0; a < ARCHES.length; a++) {
      const row = [];
      for (let l = 0; l < NLOD; l++) {
        // only build the buckets the collapse table can actually address
        if (COLLAPSE[l].indexOf(a) < 0) { row.push(null); continue; }
        const geo = buildArchGeometry(chars, ARCHES[a], l);
        const cap = CAPS[l];
        const im = new THREE.InstancedMesh(geo, material, cap);
        im.name = 'crowd_' + ARCHES[a].key + '_lod' + l;
        im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
        im.instanceColor.setUsage(THREE.DynamicDrawUsage);
        im.frustumCulled = false;
        // Full VAT bodies are expensive shadow casters. LOD0 keeps grounded
        // silhouettes around the hero; the mid/far ranks inherit terrain AO
        // and the massed near-rank shadow instead of multiplying their tris.
        im.castShadow = l === 0;
        im.receiveShadow = true;
        im.customDepthMaterial = depthMat;
        im.count = 0;
        const animA = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
        const animB = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
        const misc = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
        animA.setUsage(THREE.DynamicDrawUsage);
        animB.setUsage(THREE.DynamicDrawUsage);
        misc.setUsage(THREE.DynamicDrawUsage);
        geo.setAttribute('aAnimA', animA);
        geo.setAttribute('aAnimB', animB);
        geo.setAttribute('aMisc', misc);
        totalTris += geo.userData.triangles | 0;
        const rec = {
          mesh: im, geo, cap, n: 0, lod: l, arch: a,
          mat: im.instanceMatrix.array, col: im.instanceColor.array,
          A: animA.array, B: animB.array, M: misc.array,
          animA, animB, misc, tris: geo.userData.triangles | 0,
        };
        row.push(rec);
        groups.push(im);
        w.scene.add(im);
      }
      bucket.push(row);
    }
    buildMs = +(performance.now() - t0).toFixed(1);
    built = true;
    if (api) { api.built = true; api.stats.buildMs = buildMs; api.stats.geoTriangles = totalTris; }
    console.log('[crowd] built', groups.length, 'buckets in', buildMs, 'ms,',
      totalTris, 'unique tris, VAT', vat.width + 'x' + vat.height);
    return true;
  }

  /* ------------------------------------------------------------- helpers */

  function archFor(kind, h) {
    switch (kind) {
      case KIND.ARCHER: return 4;
      case KIND.SERGEANT: return 5;
      case KIND.OFFICER: case KIND.ALLY_OFFICER: return 6;
      case KIND.GENERAL: return 7;
      default: {
        // 3 in 8 spearmen, 2 dao, 2 halberd, 1 banner-bearer — the standard
        // bearers are what make a mass of infantry read as an ARMY
        const g = h & 7;
        return g === 7 ? 3 : g % 3;
      }
    }
  }

  function refreshCache(w, id) {
    const e = w.entities;
    const h = ((e.spawnKey[id] * 2654435761) ^ (e.variant[id] * 40503)) >>> 0;
    cache.uid[id] = e.uid[id];
    cache.arch[id] = archFor(e.kind[id], h >>> 3);
    cache.lod[id] = -1;
    // Entity slots are recycled. Never let a new warrior inherit the previous
    // occupant's camera-occlusion fade.
    cache.occ[id] = 1;
    cache.occHold[id] = 0;
    cache.seed[id] = (h & 1023) / 1023 * 6.283;
    variantTint(e.variant[id] || (h & 0xffff), _col);
    cache.tint[id * 3] = _col.r; cache.tint[id * 3 + 1] = _col.g; cache.tint[id * 3 + 2] = _col.b;
  }

  function lodFor(id, d) {
    const cur = cache.lod[id];
    let l;
    if (cur < 0) l = d < LOD_DIST[0] ? 0 : d < LOD_DIST[1] ? 1 : 2;
    else if (cur === 0) l = d > LOD_DIST[0] + LOD_HYST ? (d > LOD_DIST[1] + LOD_HYST ? 2 : 1) : 0;
    else if (cur === 1) l = d < LOD_DIST[0] - LOD_HYST ? 0 : (d > LOD_DIST[1] + LOD_HYST ? 2 : 1);
    else l = d < LOD_DIST[1] - LOD_HYST ? (d < LOD_DIST[0] - LOD_HYST ? 0 : 1) : 2;
    cache.lod[id] = l;
    return l;
  }

  /**
   * Stabilize the camera-published lens blockers into binary per-entity
   * visibility. The rig updates after the crowd, so its typed list is
   * intentionally one frame old. Hide on the first observed blocker frame,
   * then hold briefly after clearance to reject edge chatter. Publishing only
   * 0 or 1 is deliberate: a stochastic partial body reads as corrupted armour
   * at combat distance. Every array is persistent; this path allocates nothing.
   */
  function syncOcclusion(w, realDt) {
    const mask = cache.occMask;
    mask.fill(0);

    const rig = w.camrig;
    if (rig && rig.occluders) {
      const list = rig.occluders;
      const n = Math.min(Math.max(0, rig.occluderCount | 0), list.length);
      for (let i = 0; i < n; i++) {
        const id = list[i] | 0;
        if (id >= 0 && id < N) mask[id] = 1;
      }
    }

    const dt = Math.min(OCC_MAX_DT, Math.max(0, realDt || 0));
    const e = w.entities;
    for (let i = 0; i < e.liveN; i++) {
      const id = e.live[i];
      if (id < 0 || id >= N) continue;
      // refreshCache() owns initialization for a new slot. Holding at one here
      // avoids fading stale data before that cache entry has been rebuilt.
      if (cache.uid[id] !== e.uid[id]) {
        cache.occ[id] = 1;
        cache.occHold[id] = 0;
        continue;
      }
      let hidden = mask[id] !== 0;
      if (hidden) {
        cache.occHold[id] = OCC_CLEAR_HOLD;
      } else {
        const hold = Math.max(0, cache.occHold[id] - dt);
        cache.occHold[id] = hold;
        hidden = hold > 0;
      }
      cache.occ[id] = hidden ? 0 : 1;
    }
  }

  /* ---------------------------------------------------------------- sync */

  function sync(w, realDt = 0) {
    if (!built && !build(w)) return;
    const _t0 = performance.now();
    const e = w.entities, anim = w.anim, cam = w.camera;
    const nc = vat.clipCount, base = vat.base, cnt = vat.count;
    for (let a = 0; a < bucket.length; a++) for (let l = 0; l < NLOD; l++) if (bucket[a][l]) bucket[a][l].n = 0;
    syncOcclusion(w, realDt);

    _pm.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_pm);
    for (let i = 0; i < 6; i++) {
      const p = _frustum.planes[i];
      planes[i * 4] = p.normal.x; planes[i * 4 + 1] = p.normal.y;
      planes[i * 4 + 2] = p.normal.z; planes[i * 4 + 3] = p.constant;
    }
    const cx = cam.position.x, cy = cam.position.y, cz = cam.position.z;

    let drawn = 0, culled = 0;
    const l0 = _lodN; l0[0] = 0; l0[1] = 0; l0[2] = 0;
    const pid = w.playerId;
    for (let i = 0; i < e.liveN; i++) {
      const id = e.live[i];
      if (id === pid || e.kind[id] === KIND.PLAYER) continue;
      if (drawn >= maxUnits) break;
      if (cache.uid[id] !== e.uid[id]) refreshCache(w, id);

      const x = e.x[id], y = e.y[id], z = e.z[id];
      const sc = e.scale[id] || 1;
      const dx = x - cx, dy = y - cy, dz = z - cz;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);

      // cheap sphere cull against the camera frustum
      // the camera rig runs after us, so its matrices are one frame old:
      // pad the test sphere rather than pop units at the screen edge
      const bx = x, by = y + 0.95 * sc, bz = z, r = 1.45 * sc + 2.2;
      let vis = true;
      for (let p = 0; p < 6; p++) {
        if (planes[p * 4] * bx + planes[p * 4 + 1] * by + planes[p * 4 + 2] * bz + planes[p * 4 + 3] < -r) { vis = false; break; }
      }
      if (!vis) { culled++; continue; }

      const arch = cache.arch[id];
      let lod = lodFor(id, d);
      let g = null;
      for (let k = 0; k < NLOD; k++) {
        const cand = bucket[COLLAPSE[lod][arch]][lod];
        if (cand && cand.n < cand.cap) { g = cand; break; }
        if (lod >= NLOD - 1) break;
        lod++;
      }
      if (!g) { culled++; continue; }

      const n = g.n++;
      const dead = e.state[id] === ST.DEAD;
      const fade = dead ? Math.max(0, e.fade[id]) : 1.15;
      const sink = dead ? (1 - Math.max(0, e.fade[id])) * 0.42 : 0;

      const yaw = e.yaw[id];
      const sn = Math.sin(yaw), co = Math.cos(yaw);
      const M = g.mat, mo = n * 16;
      const pt = e.pitch[id], rl = e.roll[id];
      if (pt === 0 && rl === 0) {
        // Everything standing on the floor takes this path — `src/ai/steering.js`
        // zeroes pitch/roll the moment a body lands, so it is 490 of 500 bodies
        // and it is byte-identical to what this loop wrote before the tumble
        // existed. Yaw only, no trig beyond the two above.
        M[mo] = co * sc; M[mo + 1] = 0; M[mo + 2] = -sn * sc; M[mo + 3] = 0;
        M[mo + 4] = 0; M[mo + 5] = sc; M[mo + 6] = 0; M[mo + 7] = 0;
        M[mo + 8] = sn * sc; M[mo + 9] = 0; M[mo + 10] = co * sc; M[mo + 11] = 0;
        M[mo + 12] = x; M[mo + 13] = y - sink; M[mo + 14] = z; M[mo + 15] = 1;
      } else {
        // A body in the air turns over. R = Ry(yaw) . Rx(pitch) . Rz(roll),
        // applied about a pivot at mid-body rather than at the feet: rotating a
        // man about his soles swings his head through a two-metre arc and puts
        // it under the floor. 0.95 * sc is the same body centre the frustum
        // sphere above already uses.
        const sb = Math.sin(pt), cb = Math.cos(pt);
        const sg = Math.sin(rl), cg = Math.cos(rl);
        const r00 = co * cg + sn * sb * sg, r01 = -co * sg + sn * sb * cg, r02 = sn * cb;
        const r10 = cb * sg, r11 = cb * cg, r12 = -sb;
        const r20 = -sn * cg + co * sb * sg, r21 = sn * sg + co * sb * cg, r22 = co * cb;
        M[mo] = r00 * sc; M[mo + 1] = r10 * sc; M[mo + 2] = r20 * sc; M[mo + 3] = 0;
        M[mo + 4] = r01 * sc; M[mo + 5] = r11 * sc; M[mo + 6] = r21 * sc; M[mo + 7] = 0;
        M[mo + 8] = r02 * sc; M[mo + 9] = r12 * sc; M[mo + 10] = r22 * sc; M[mo + 11] = 0;
        // translation = pos + pivot - R * pivot, and R * (0,pv,0) is column 1
        const pv = 0.95 * sc;
        M[mo + 12] = x - r01 * pv;
        M[mo + 13] = y - sink + pv - r11 * pv;
        M[mo + 14] = z - r21 * pv;
        M[mo + 15] = 1;
      }

      const co3 = n * 3, tc = id * 3;
      g.col[co3] = cache.tint[tc]; g.col[co3 + 1] = cache.tint[tc + 1]; g.col[co3 + 2] = cache.tint[tc + 2];

      anim.sampleInstance(id, _a5);
      const vk = KIND_TO_VAT[e.kind[id]] * nc;
      const ao = n * 4;
      const ia = vk + _a5[0], ib = vk + _a5[2];
      g.A[ao] = base[ia]; g.A[ao + 1] = cnt[ia]; g.A[ao + 2] = _a5[1]; g.A[ao + 3] = cache.occ[id];
      g.B[ao] = base[ib]; g.B[ao + 1] = cnt[ib]; g.B[ao + 2] = _a5[3]; g.B[ao + 3] = _a5[4];
      g.M[ao] = e.team[id] > 2 ? 0 : e.team[id];
      g.M[ao + 1] = e.flash[id];
      g.M[ao + 2] = fade;
      g.M[ao + 3] = cache.seed[id];

      l0[lod]++;
      drawn++;
    }

    let live = 0, tris = 0, shadowDraws = 0;
    for (let a = 0; a < bucket.length; a++) {
      for (let l = 0; l < NLOD; l++) {
        const g = bucket[a][l];
        if (!g) continue;
        g.mesh.count = g.n;
        if (g.n > 0) {
          g.mesh.instanceMatrix.needsUpdate = true;
          g.mesh.instanceColor.needsUpdate = true;
          g.animA.needsUpdate = true; g.animB.needsUpdate = true; g.misc.needsUpdate = true;
          live++; tris += g.n * g.tris;
          if (g.mesh.castShadow) shadowDraws++;
        }
      }
    }
    api.visible = drawn;
    const s = api.stats;
    s.lod0 = l0[0]; s.lod1 = l0[1]; s.lod2 = l0[2];
    s.culled = culled; s.drawn = drawn; s.buckets = live; s.triangles = tris;
    // what the crowd actually costs the frame: one draw per non-empty bucket,
    // plus one more for each bucket that also casts into the shadow map
    s.colorDraws = live; s.shadowDraws = shadowDraws; s.drawCalls = live + shadowDraws;
    const ms = performance.now() - _t0;
    s.syncMsLast = +ms.toFixed(3);
    s.syncMs = +(s.syncMs * 0.9 + ms * 0.1).toFixed(3);
  }

  /* ================================================================ system */

  return {
    name: 'crowd', order: ORDER.CROWD,

    init(w) {
      api = {
        groups, visible: 0, max: maxUnits, built: false,
        stats: { lod0: 0, lod1: 0, lod2: 0, culled: 0, drawn: 0, buckets: 0, triangles: 0,
          colorDraws: 0, shadowDraws: 0, drawCalls: 0, syncMs: 0, syncMsLast: 0, buildMs: 0, geoTriangles: 0 },
        arches: ARCHES,
        sync() { if (w) sync(w); },
        setMax(n) { maxUnits = Math.max(0, n | 0); api.max = maxUnits; },
        setVisible(v) { for (const g of groups) g.visible = v; },
        get material() { return material; },
        get vat() { return vat; },
        get bucket() { return bucket; },
        dispose() { for (const g of groups) { g.geometry.dispose(); w.scene.remove(g); } groups.length = 0; },
      };
      w.crowd = api;
    },

    lateUpdate(w, dt, alpha, realDt) {
      sync(w, realDt);
    },

    dispose() {
      for (const g of groups) g.geometry.dispose();
      material?.dispose(); depthMat?.dispose(); atlas?.dispose();
    },
  };
}
